import React, { useEffect, useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { fetchPagesForSelection, searchPages, InvalidatePagesForSelection } from 'actions/pages';

import _ from 'lodash';
import cx from "classnames";
import dateformat from "dateformat";
import { Row, Col, Button } from 'react-bootstrap';
import { IKImage } from "imagekitio-react";
import LinesEllipsis from 'react-lines-ellipsis'
import Tooltip from 'components/Tooltip';
import Truncate from "react-truncate";

import { fetchAllStoryboardLists, invalidateStoryboardList, selectStoryboardList } from 'actions/storyboardList'
import { fetchStoryboards, invalidateStoryboardsState } from 'actions/storyboard';
import { uploadStoryboardPage, removeSelectedPage } from 'actions/content';

import InfiniteScroll from "components/InfiniteScroll";

import { SMART_LISTS } from 'constants/smartLists';
import { Loader } from "components/ApplicationLoader";

import shortid from 'shortid';
import styles from './storyboard.module.scss'
import FileService from 'services/file.service';
import { INTERACTIVE_CONTENT_TYPES } from 'constants/storyboardType';

const defaultContentImage = `/cleverstory/images/default-content.png`;

const Storyboard = ({ selection, onChange }) => {
  const dispatch = useDispatch();
  const [storyboard, setStoryboard] = useState(null);

  useEffect(() => {
    return () => {
      dispatch(invalidateStoryboardList());
      dispatch(invalidateStoryboardsState());
    }
  }, [])

  const handleStoryboardSelection = (storyboard) => {
    if (!INTERACTIVE_CONTENT_TYPES.includes(storyboard.type)) {
      setStoryboard(storyboard);
    } else {
      handleItemSelection(storyboard);
    }
  }

  const uploadedAssets = useSelector((state) =>
    (state.content.uploads || []).filter(
      (asset) => _.get(asset, 'asset.entityType') === 'page' || _.get(asset, 'asset.entityType') === 'storyboard'
    )
  );

  const handleItemSelection = (content) => {

    const existingAssset = _.some(uploadedAssets, (assetContent) => _.get(assetContent, 'asset.id') === content.id); // Returns true

    if (!existingAssset) {
      const assetRequest = {
        id: shortid(),
        type: 'PAGE',
        source: 'CLEVERSTORY',
        asset: {
          ...content
        }
      }
      dispatch(uploadStoryboardPage(assetRequest));
    }
  }

  return (
    <>
      {!storyboard &&
        <StoryboardView
          selection={selection}
          onSelection={handleStoryboardSelection}
          uploadedAssets={uploadedAssets}
        />
      }

      {storyboard &&
        <LandingPageView
          storyboard={storyboard}
          selection={selection}
          onSelection={handleItemSelection}
          uploadedAssets={uploadedAssets}
          onBack={() => {
            setStoryboard(null);
          }}
        />
      }
    </>
  )
}

function StoryboardView({ selection, onSelection, uploadedAssets }) {
  const dispatch = useDispatch()
  const [stickyHeader, setStickyHeader] = useState(false)
  const scrollContainerRef = useRef(null)

  const storyboardLists = useSelector((state) => _.get(state, "storyboardList.items", []))
  const activeListId = useSelector((state) => _.get(state.storyboardList, 'item.id', null))
  const storyboards = useSelector(state => _.get(state, 'storyboards.items', []));
  const loading = useSelector(state => _.get(state, 'storyboards.loading', false));
  const pagination = useSelector(state => _.get(state, 'storyboards.pagination', {}));
  const hasMoreItems = _.get(pagination, "current") < _.get(pagination, "totalPages") - 1;

  useEffect(() => {
    handleFetchStoryboards(activeListId, 0);
    dispatch(fetchAllStoryboardLists());

    scrollContainerRef.current.addEventListener('scroll', handleScroll);

    return () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener('scroll', handleScroll);
      }
    }
  }, [])

  const handleScroll = () => {
    var header = scrollContainerRef.current.scrollTop;
    if (header > 20) {
      setStickyHeader(true)
    } else {
      setStickyHeader(false)
    }
  }

  const loadMoreStoryboards = () => {
    const nextPage = pagination.current + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }
    handleFetchStoryboards(activeListId, nextPage);
  }

  const handleFetchStoryboards = (listId, page) => {
    var request = {
      list: listId,
      page: page,
      limit: 10,
      sort: 'createdDate',
      order: 'desc'
    }

    dispatch(fetchStoryboards(request));
  }

  const handleCardClick = (storyboard) => {
    onSelection(storyboard);
  }

  const handleDelete = (id) => {
    console.log("Id", id)
    dispatch(removeSelectedPage(id));
  }

  return (
    <div className={styles.storyboardContainer} ref={scrollContainerRef}>
      <Row className={styles.layoutRow}>
        <Col className={styles.assetsLayout} xs={8}>
          <div className={cx(styles.smartlistContainer, { [styles.sticky]: stickyHeader })} >
            <div className={styles.listContainer}>
              <div className={styles.listContent}>
                {SMART_LISTS.map((list, index) => {
                  return (
                    <StoryboardList
                      key={index}
                      index={index}
                      list={list}
                      handleFetchStoryboards={() => handleFetchStoryboards(list.id, 0)}
                    />
                  );
                })}
                {storyboardLists.map((list, index) => {
                  return (
                    <StoryboardList
                      key={index}
                      index={index}
                      list={list}
                      handleFetchStoryboards={() => handleFetchStoryboards(list.id, 0)}
                    />
                  );
                })}
              </div>
            </div>
          </div>

          <div className={styles.storyboardCardContainer}>
            {loading && storyboards.length === 0 &&
              <div className={styles.loadingAnimationContainer}>
                <Loader className={styles.loadingAnimation} title="Loading storyboards..." />
              </div>
            }

            {!loading && storyboards.length === 0 && renderEmptyMessage()}

            {storyboards.length > 0 &&
              <div >
                <InfiniteScroll
                  className={styles.scrollable}
                  loading={loading}
                  hasMore={hasMoreItems}
                  loadMore={() => {
                    loadMoreStoryboards();
                  }}
                >
                  {storyboards.map(item => {
                    var selected = selection.some(data => data.id === item.id);
                    return (
                      <StoryboardCard
                        key={item.id}
                        storyboard={item}
                        selected={selected}
                        onClick={handleCardClick}
                      />
                    )
                  })}
                </InfiniteScroll>
              </div>
            }
          </div>
        </Col>
        <Col className={styles.selectionList} xs={4}>
            <div className={styles.header}>
              <span className={styles.assetType}>Selected:</span>
              <span className={styles.folderPath}>{_.size(uploadedAssets)} Assets</span>
            </div>
            <div className={styles.selectedAssetView}>
              {_.map(uploadedAssets, (asset) => {
                return (
                  <AssetSelection
                    key={_.get(asset, 'id', null)}
                    className={styles.assetCard}
                    id={_.get(asset, 'id', null)}
                    assetMapping={asset}
                    onClick={() => { console.log('') }}
                    handleDelete={handleDelete}
                  />
                )
              })
              }
            </div>
        </Col>
      </Row>
    </div>
  )
}

const renderEmptyMessage = () => {
  return (
    <div className={styles.emptyMessageContainer}>
      <div className={styles.emptyMessage}>
        <video className={styles.emptyImage} loop autoPlay playsInline muted>
          <source src="/images/Blackhole.webm" type="video/webm" />
          <source src="/images/Blackhole.mp4" type="video/mp4" />
        </video>
        <div className={styles.errorMessageContent}>
          <div className={styles.errorMessageTitle}>
            <span>Do<br /> you believe in</span>
            <span>Black<br /> Holes?</span>
          </div>
          <div className={styles.errorMessageSubText}>
            <span>You better do, because nothing<br /> else explains this empty screen.<br /></span>
            <span>...That or you haven’t added <br /> anything here yet.</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function StoryboardList(props) {
  const ref = useRef(null);
  const dispatch = useDispatch();
  const activeListId = useSelector((state) => _.get(state, 'storyboardList.item.id', null));

  const handleFolderClick = (id) => {
    props.handleFetchStoryboards(id, 0)
    dispatch(selectStoryboardList(id));
  }

  const truncate = (name) => {
    return name.length > 30 ? name.substring(0, 25) + "..." : name;
  }

  return (
    <div
      id={props.list.id}
      onClick={() => handleFolderClick(props.list.id)}
      className={cx(styles.storyboardList, { [styles.active]: props.list.id == activeListId })}
    >

      <div ref={ref} className={styles.ref}>
        <span className={styles.name}>{truncate(props.list.name)}</span>
      </div>
    </div>
  )
}

const StoryboardCard = ({ storyboard, onClick, selected }) => {
  const icon = _.get(storyboard, 'page.icon.url') || null;
  const published = _.get(storyboard, 'page.published');
  const changes = _.get(storyboard, 'page.changes');
  const type = _.get(storyboard, 'type', '');
  const [tooltip, setTooltip] = useState(false);

  const renderStoryboardType = () => {
    if (type === 'LANDING_PAGE') {
      return 'Landing page';
    } else if (INTERACTIVE_CONTENT_TYPES.includes(type)) {
      return 'Interactive content';
    } else if (type === 'SPATIAL') {
      return 'Spatial';
    } else if (type === 'FLIP_BOOK') {
      return 'Flipbook';
    }
    else {
      return 'Resource center';
    }
  }

  const handleCardClick = () => {
    onClick(storyboard);
  }

  const handleTooltip = (isTruncated) => {
    setTooltip(isTruncated)
  }

  return (
    <div className={cx(styles.storyboardCard, {
      [styles.selected]: selected
    })} onClick={handleCardClick}>
      {!INTERACTIVE_CONTENT_TYPES.includes(type) &&
        <>
          <div className={cx(styles.layer, styles.layer1)}></div>
          <div className={cx(styles.layer, styles.layer2)}></div>
        </>
      }
      <div className={cx(styles.cardContainer)}>
        <IKImage
          className={styles.icon}
          path={icon || defaultContentImage}
          transformation={[{
            "width": "600"
          }]}
        />

        <div className={styles.details}>
          <div className={styles.top}>
            <Tooltip placement="top" content={storyboard.name} disabled={!tooltip} className={styles.tooltip}>
              <span className={styles.title}>
                <Truncate onTruncate={handleTooltip} lines={2}>
                  {storyboard.name}
                </Truncate>
              </span>
            </Tooltip>
            {published &&
              <label className={cx(styles.status, styles.live)}>LIVE</label>
            }
          </div>

          <p className={styles.contentType}>{renderStoryboardType()}</p>

          <div className={styles.updatedDate}>
            Updated: {dateformat(_.get(storyboard, 'page.modifiedDate'), "dd mmm yyyy")}
            {(changes && !published) &&
              <label className={cx(styles.status, styles.draft)}>DRAFT</label>
            }
          </div>
        </div>
      </div>
    </div>
  )
}

const LandingPageView = ({ storyboard, selection, onSelection, onBack, uploadedAssets }) => {
  const dispatch = useDispatch();

  const [stickyHeader, setStickyHeader] = useState(false);
  const scrollContainerRef = useRef(null);

  const loading = useSelector(state => _.get(state, 'pages.selection.loading'));
  const pages = useSelector(state => _.get(state, 'pages.selection.items', []));
  const pagination = useSelector(state => _.get(state, 'pages.selection.pagination', {}));
  const hasMorePages = _.get(pagination, "currentPage") < _.get(pagination, "totalPages") - 1;

  useEffect(() => {

    if (storyboard) {
      var request = {
        storyboard: storyboard.id
      }

      dispatch(fetchPagesForSelection(request, 0, 20));
    }

    scrollContainerRef.current.addEventListener('scroll', handleScroll);

    return () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener('scroll', handleScroll);
      }
      dispatch(InvalidatePagesForSelection());
    }
  }, [])

  const loadMorePages = () => {
    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }
    if (storyboard) {
      var request = {
        storyboard: storyboard.id
      }
      dispatch(fetchPagesForSelection(request, nextPage, 20));
    }
  }

  const handleScroll = () => {
    var header = scrollContainerRef.current.scrollTop;
    if (header > 20) {
      setStickyHeader(true)
    } else {
      setStickyHeader(false)
    }
  }

  const handleDelete = (id) => {
    console.log("Id", id);
    dispatch(removeSelectedPage(id));
  }

  return (
    <div className={styles.storyboardContainer} ref={scrollContainerRef}>
      <Row className={styles.layoutRow}>
        <Col className={styles.assetsLayout} xs={8}>
          <div className={styles.landingPageContainer}>
            <div className={cx(styles.header, { [styles.sticky]: stickyHeader })} >
              <button className={styles.backButton} onClick={onBack} >
                <i className={cx('cleverstory-back', styles.backIcon)} />
                <div className={styles.storyboardName}> 
                  <LinesEllipsis basedOn="letters" text={_.get(storyboard, 'name')} maxLine='1' ellipsis='...' />
                </div>
              </button>
            </div>
            {loading && _.size(pages) === 0 &&
              <Loader className={styles.loading} title="Loading pages..." />
            }
            {_.size(pages) > 0 && (
              <div className={styles.pageScrollContainer}>
                <InfiniteScroll
                  hasMore={hasMorePages}
                  loading={loading}
                  loadMore={() => loadMorePages()}
                  hideLoadMoreButton={true}
                  className={styles.landingPageCardContainer}
                >
                  {pages?.map(page => {
                    var selected = selection.some(data => data.id === page.id);
                    return (
                      <LandingPageCard
                        key={page.id}
                        page={page}
                        selected={selected}
                        onSelection={onSelection}
                      />
                    );
                  })}
                </InfiniteScroll>
              </div>
            )}
          </div>
        </Col>
        <Col className={styles.selectionList} xs={4}>
            <div className={styles.header}>
              <span className={styles.assetType}>Selected:</span>
              <span className={styles.folderPath}>{_.size(uploadedAssets)} Assets</span>
            </div>
            <div className={styles.selectedAssetView}>
              {_.map(uploadedAssets, (asset) => {
                return (
                  <AssetSelection
                    key={_.get(asset, 'id', null)}
                    className={styles.assetCard}
                    id={_.get(asset, 'id', null)}
                    assetMapping={asset}
                    onClick={() => { console.log('') }}
                    handleDelete={handleDelete}
                  />
                )
              })
              }
            </div>
        </Col>
      </Row>
    </div>
  )
}

const LandingPageCard = ({ page, selected, onSelection }) => {
  const icon = _.get(page, 'icon.url');
  const published = _.get(page, 'published');
  const changes = _.get(page, 'changes');
  const hidden = _.get(page, 'hidden');
  const [tooltip, setTooltip] = useState(false);

  const handleSelection = () => {
    var request = {
      ...page,
      type: _.get(page, 'storyboard.type', 'LANDING_PAGE')
    }

    onSelection(request);
  }

  const handleTooltip = (isTruncated) => {
    setTooltip(isTruncated)
  }

  return (
    <div onClick={handleSelection} className={cx(styles.storyboardCard, styles.landingPageCard, { [styles.selectStoryboardCard]: selected })}>
      <div className={styles.cardContainer}>
        <IKImage
          className={styles.icon}
          path={icon || defaultContentImage}
          transformation={[{
            "width": "600"
          }]}
        />
        <div className={styles.details}>
          <div className={styles.top}>
            <Tooltip placement="top" content={page.name} disabled={!tooltip} className={styles.tooltip}>
              <span className={styles.title}>
                <Truncate onTruncate={handleTooltip} lines={2}>
                  {page.name}
                </Truncate>
              </span>
            </Tooltip>
            {published &&
              <label className={cx(styles.status, styles.live)}>LIVE</label>
            }
            {hidden &&
              <label className={cx(styles.status, styles.hidden)}>HIDDEN</label>
            }
          </div>  
          <div className={styles.updatedDate}>
            Updated: {dateformat(_.get(page.modifiedDate), "dd mmm yyyy")}
            {(changes && !published) &&
              <label className={cx(styles.status, styles.draft)}>DRAFT</label>
            }
          </div>
        </div>
      </div>
    </div>
  )
}

const AssetSelection = ({ assetMapping, key, id, selected, handleDelete }) => {

  let icon = _.get(assetMapping, 'asset.icon.url') || defaultContentImage;
  const name = _.get(assetMapping, 'asset.name', '');
  const [tooltip, setTooltip] = useState(false);

  if(INTERACTIVE_CONTENT_TYPES.includes(_.get(assetMapping, 'asset.type'))) {
    icon = _.get(assetMapping, 'asset.page.icon.url') || defaultContentImage;
  }

  const handleTooltip = (isTruncated) => {
    setTooltip(isTruncated)
  }

  return (
    <div key={key} id={id} className={cx(styles.assetCardWrapper, 'cs-asset-card', { ['cs-selected-asset-card']: selected }, {
      [styles.selected]: selected
    })}>
      <Button className={styles.deleteAssetButton} variant='info'  onClick={() => handleDelete(_.get(assetMapping, 'asset.id'))}>
        <i className={cx(styles.closeIcon, 'cleverstory-close')} />
      </Button>
      <div className={styles.iconWrapper}>
        <IKImage
          className={styles.icon}
          path={icon}
        />
      </div>
      <div className={styles.detailsContainer}>
        <Tooltip placement="top" disabled={!tooltip} content={name} className={styles.tooltip}>
          <span className={styles.name}>
            <Truncate onTruncate={handleTooltip} lines={1}>
              {name}
            </Truncate>
          </span>
        </Tooltip>
        <ContentMetadata content={assetMapping} />
      </div>
    </div>
  )
}

const renderStoryboardType = (type) => {
  if (type === 'LANDING_PAGE') {
    return 'Landing page';
  } else if (INTERACTIVE_CONTENT_TYPES.includes(type)) {
    return 'Interactive content';
  } else if (type === 'RESOURCE_CENTER') {
    return 'Resource center';
  } else if (type === 'SPATIAL') {
    return 'Spatial';
  } else {
    return 'Flipbook';
  }
}

export const ContentMetadata = ({ content, className }) => {
  const metaData = _.get(content, 'asset.metadata', {})
  let fileMetaData = FileService.fileMetaData(metaData);
  let storyboardType = _.get(content, 'asset.type');

  return (
    <div className={styles.metaData}>
      <div className={styles.details}>
        <div className={styles.type}>
          {(_.get(content, 'asset.entityType') === 'asset' || _.get(content, 'entityType') === 'asset' ) &&
            <>
              <i className={cx(styles.fileIcon, `${FileService.fileIcon(metaData)}`)} />
              <span className={styles.textFormat} >{FileService.fileType(metaData)}</span>
            </>
          }
          {(_.get(content, 'asset.entityType') === 'page' || _.get(content, 'entityType') === 'page' || _.get(content, 'asset.entityType') === 'storyboard') &&
            <>
              <i className={cx(styles.fileIcon, `${FileService.storyboardIcon(storyboardType)}`)} />
              <span className={styles.textFormat} >{renderStoryboardType(storyboardType)}</span>
            </>
          }
        </div>


        {fileMetaData && <i className={styles.seperator}></i>}
        {fileMetaData && <span className={styles.contentMetadata}>{fileMetaData}</span>}
      </div>
      <span className={styles.date}>{dateformat(metaData.modified_date, 'dd mmm yyyy')}</span>
    </div>
  );
}

export default Storyboard;